import {
  NAV_COLLAPSED_WIDTH,
  NAV_GAP,
  RIGHT_MARGIN,
} from "@/components/chat/SidebarChat/chatWidgetLayout";
import { FRANKLIN_UPDATE_MAX_WIDTH } from "@/components/chat/SidebarChat/franklinUpdateVisual";
import { BOTTOM_RIGHT_EXCLUSION_VAR } from "./bottomRightExclusion";

/**
 * Layout contract for Franklin's bottom-corner surfaces: stacking, shared lane
 * width and collision-free toast placement.
 *
 * Four surfaces share that corner and each lives in its own body portal: the
 * collapsed chat pill, the open chat panel, the lane (upload/processing card +
 * chat nudge) and the toaster. Portals at the SAME z-index are ordered by which
 * node mounted last, which is how the upload card ended up drawing over an open
 * chat panel — the lane node is created when the first upload starts, long after
 * the chat mounted. These tokens state the order instead of inheriting it from
 * mount timing.
 *
 * Order, front to back:
 *   modals / Header / BulkActionBar (z-50, not ours)  — always win
 *   open chat panel                                   — the user is reading it
 *   toasts                                            — transient, above the lane
 *   lane cards + collapsed chat pill
 *
 * Everything here stays below 50 so a dialog still covers the whole corner.
 * See `BottomRightLane.tsx` (the lane), `ui/sonner.tsx` (the toaster) and
 * `chat/SidebarChat/ChatWidget.tsx` (pill + panel).
 */
export const CORNER_Z = {
  /** Lane cards: upload/processing progress and the chat nudge. */
  lane: 40,
  /** The collapsed chat pill. Toasts and lane cards sit above it, by design. */
  chatCollapsed: 40,
  /**
   * Toasts while the chat panel is open — below the panel, still above the lane.
   * Closed-chat toasts keep `toastsDefault`.
   */
  toastsOverLane: 44,
  /**
   * The open chat panel (windowed or fullscreen) owns the top of the corner:
   * once the user opens it, nothing transient may cover what they are reading.
   */
  chatOpen: 46,
  /**
   * Toasts with the chat closed: above the modal layer, matching the toast
   * viewport this app shipped before it moved to sonner (`z-[100]`), so an
   * error toast raised from inside a dialog is still readable.
   */
  toastsDefault: 100,
} as const;

/**
 * Width of the docked chat column. The lane follows the resizable panel; the
 * collapsed launcher and Franklin update cards deliberately use their own
 * compact width and remain right-aligned inside this column.
 */
export const CORNER_WIDTH = 400;

/** CSS variable carrying the corner's live width. */
export const CORNER_WIDTH_VAR = "--corner-width";

/** `width` expression for corner surfaces; `CORNER_WIDTH` until the chat publishes. */
export const cornerWidth = `var(${CORNER_WIDTH_VAR}, ${CORNER_WIDTH}px)`;

/**
 * Left edge the corner may never cross — the collapsed nav rail plus its gap,
 * the same floor `bottomRightPosition` gives the chat pill.
 */
const CORNER_MIN_LEFT = NAV_COLLAPSED_WIDTH + NAV_GAP;

/**
 * Right edge of Franklin's corner after any non-chat rail is reserved.
 *
 * The reservation is clamped, because a right-side rail can be most of the
 * window: the building-data attribute panel and the PM issue detail panel both
 * resize to 65 % of the viewport, and on a narrow window `RIGHT_MARGIN +
 * exclusion` alone pushed the lane's fixed-width column off the left edge of
 * the screen, where it is clipped and unclickable. The lane gives up its
 * reservation rather than the viewport. The outer `max()` covers a window too
 * narrow for the column itself, where the inner term goes negative — the lane's
 * own `max-w` shrinks it from there.
 */
export const cornerRightPosition = `max(0px, min(calc(${RIGHT_MARGIN}px + var(${BOTTOM_RIGHT_EXCLUSION_VAR}, 0px)), calc(100vw - ${cornerWidth} - ${CORNER_MIN_LEFT}px)))`;

/**
 * Publish the chat's current docked width so the lane follows it. Called by
 * `ChatWidget`; nothing else owns this value. Fullscreen is NOT a corner width,
 * so the widget leaves the last docked width in place while expanded.
 */
export function setCornerWidth(px: number): void {
  document.documentElement.style.setProperty(CORNER_WIDTH_VAR, `${Math.round(px)}px`);
}

/**
 * CSS variable carrying the toaster's z-index. sonner renders its own fixed
 * viewport outside our tree, so the corner publishes the value and the toaster
 * reads it — the same one-way trick `--bottom-right-lane` uses for its offset,
 * and it keeps the chat's open/closed state out of `ui/sonner.tsx`.
 */
export const TOAST_Z_VAR = "--corner-toast-z";

/** Horizontal toast position, switched from the corner to beside an open chat panel. */
export const TOAST_LEFT_VAR = "--corner-toast-left";
export const TOAST_RIGHT_VAR = "--corner-toast-right";

/** Breathing room between an open chat panel and the Franklin update column. */
export const TOAST_PANEL_GAP = 16;

/** `z-index` expression for the toaster; `toastsDefault` until the chat opens. */
export const toastZIndex = `var(${TOAST_Z_VAR}, ${CORNER_Z.toastsDefault})`;

export const toastLeftPosition = `var(${TOAST_LEFT_VAR}, auto)`;
export const toastRightPosition = `var(${TOAST_RIGHT_VAR}, ${cornerRightPosition})`;

/**
 * Publish the open panel's position. The toaster (as wide as the widest card;
 * each toast right-aligns in it) moves immediately to its left and
 * also steps below it in z-order, so even a dragged panel can never have an
 * update or error painted in front of the conversation.
 * Called by `ChatWidget`; nothing else owns this layout state.
 */
export function setChatPanelOpen(open: boolean, panelLeft?: number): void {
  const root = document.documentElement;
  if (open) {
    const besidePanel = Math.max(
      RIGHT_MARGIN,
      Math.round(panelLeft ?? RIGHT_MARGIN) - FRANKLIN_UPDATE_MAX_WIDTH - TOAST_PANEL_GAP
    );
    root.style.setProperty(TOAST_Z_VAR, String(CORNER_Z.toastsOverLane));
    root.style.setProperty(TOAST_LEFT_VAR, `${besidePanel}px`);
    root.style.setProperty(TOAST_RIGHT_VAR, "auto");
    return;
  }
  root.style.removeProperty(TOAST_Z_VAR);
  root.style.removeProperty(TOAST_LEFT_VAR);
  root.style.removeProperty(TOAST_RIGHT_VAR);
}
